import { ArrowRightOutlined } from '@ant-design/icons';
import {
  Button, Flex,
  Typography, Popover,
} from 'antd';
import dayjs from 'dayjs';
import useIsMobile from 'hooks/useIsMobile';
import React, { useState } from 'react';
import { formatDate } from 'utils/date';
import { sanitizeHtmlContent } from 'utils/sanitizeHtml';

const { Text, Paragraph } = Typography;

// Custom Email Icon SVG Component
function ClickedEmailIcon({ style, ...props }) {
  return (
    <svg width="18" height="18" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} {...props}>
      <path d="M33.3789 7.7168H34.5L34.6533 7.72461C35.4097 7.80141 36 8.44013 36 9.2168V34.502C36 35.2786 35.4097 35.9173 34.6533 35.9941L34.5 36.002H1.5C0.671573 36.002 0 35.3304 0 34.502V9.2168C0 8.38837 0.671573 7.7168 1.5 7.7168H2.62012L18 0.273438L33.3789 7.7168ZM18 19.3047L2.7002 11.8984V33.3018H33.2998V11.8984L18 19.3047ZM4.0791 9.78906L18 16.5264L31.9199 9.78906L18 3.05176L4.0791 9.78906Z" fill="black" fillOpacity="0.85" />
    </svg>

  );
}
function OpEmailIcon({ style, ...props }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} {...props}>
      <path id="Union" d="M16.6895 3.8584H17.25L17.3267 3.8623C17.7049 3.9007 18 4.22007 18 4.6084V17.251C18 17.6393 17.7049 17.9587 17.3267 17.9971L17.25 18.001H0.75C0.335787 18.001 0 17.6652 0 17.251V4.6084C0 4.19418 0.335786 3.8584 0.75 3.8584H1.31006L9 0.136719L16.6895 3.8584ZM9 9.65234L1.3501 5.94922V16.6509H16.6499V5.94922L9 9.65234ZM5.09619 6.37354L9 8.26318L12.9038 6.37354V5.43945H5.09619V6.37354ZM2.03955 4.89453L3.84619 5.76855V4.68945C3.84619 4.41331 4.07005 4.18945 4.34619 4.18945H13.6538C13.93 4.18945 14.1538 4.41331 14.1538 4.68945V5.76855L15.96 4.89453L9 1.52588L2.03955 4.89453Z" fill="black" fillOpacity="0.85" />
    </svg>

  );
}

function EmailEvent({ event: { data: email, email_stats: stats, occurred_at: date } }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Use mock data if email_stats doesn't exist
  const emailWithStats = {
    ...email,
    email_stats: stats,
  };

  // Sanitize the HTML and handle quoted replies in one step
  const html = email.body_html ? sanitizeHtmlContent(email.body_html) : '-';

  // Generate stats tooltip content
  const getStatsTooltip = () => {
    const stats = emailWithStats.email_stats;
    if (!stats || (stats.open_times === 0 && stats.click_times === 0)) {
      return null;
    }

    const lines = [];

    if (stats.open_times > 0) {
      const recipientName = emailWithStats.to[0] || 'recipient';
      const times = stats.open_times > 5 ? '5+ times' : `${stats.open_times} ${stats.open_times === 1 ? 'time' : 'times'}`;
      const lastOpened = stats.last_opened_at ? dayjs(stats.last_opened_at).format('MMM DD, YYYY') : '';
      lines.push(
        <div key="opened">
          <div>
            Opened by
            {' '}
            {recipientName}
          </div>
          {lastOpened && (
            <div>
              (
              {times}
              , latest
              {' '}
              {lastOpened}
              )
            </div>
          )}
        </div>,
      );
    }

    if (stats.click_times > 0) {
      const recipientName = emailWithStats.to[0] || 'recipient';
      const times = stats.click_times > 5 ? '5+ times' : `${stats.click_times} ${stats.click_times === 1 ? 'time' : 'times'}`;
      const lastClicked = stats.last_clicked_at ? dayjs(stats.last_clicked_at).format('MMM DD, YYYY') : '';
      lines.push(
        <div key="clicked">
          <div>
            Clicked by
            {' '}
            {' '}
            {recipientName}
          </div>
          {lastClicked && (
            <div>
              (
              {times}
              , latest
              {' '}
              {' '}
              {lastClicked}
              )
            </div>
          )}
        </div>,
      );
    }

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px',
      }}
      >
        {lines}
      </div>
    );
  };

  const statsTooltip = getStatsTooltip();
  const hasStats = emailWithStats.email_stats && (emailWithStats.email_stats.open_times > 0 || emailWithStats.email_stats.click_times > 0);

  // Choose the appropriate icon based on email stats
  const IconComponent = emailWithStats.email_stats && emailWithStats.email_stats.click_times > 0
    ? ClickedEmailIcon
    : OpEmailIcon;

  return (
    <Flex
      vertical
      gap={4}
      className="ml-3 bg-white rounded py-2 px-3 w-full"
      onClick={(e) => {
        if (e.target.tagName === 'A') {
          return;
        }
        html.length >= 70 && setOpen(!open);
      }}
    >
      <Flex justify="space-between" vertical={isMobile} className="w-full">
        <Flex gap={12} align="center">
          <ArrowRightOutlined style={{ transform: emailWithStats.direction === 'outgoing' ? 'rotate(-45deg)' : 'rotate(45deg)' }} />
          <Text className="font-bold text-[16px]">
            {emailWithStats.subject || 'Email'}
          </Text>
        </Flex>
        <Flex gap={8} align="start">
          {hasStats && (
            <Popover
              content={statsTooltip}
              placement="top"
              overlayStyle={{ maxWidth: '300px' }}
            >
              <IconComponent
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                }}
              />
            </Popover>
          )}
          <Text>{formatDate(date)}</Text>

        </Flex>
      </Flex>

      <Flex
        vertical={isMobile}
        align="flex-start"
        gap={12}
        wrap={open ? 'wrap' : 'nowrap'}
        style={{ overflow: 'hidden' }}
      >
        <Text className="whitespace-nowrap" style={{ fontSize: 12 }}>
          to:
          {' '}
          {emailWithStats.to.join(', ')}
        </Text>
        {
          emailWithStats.cc.length > 0 && (
            <Text className="whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
              cc:
              {' '}
              {emailWithStats.cc.join(', ')}
            </Text>
          )
        }
        {
          emailWithStats.bcc.length > 0 && (
            <Text className="whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
              bcc:
              {' '}
              {emailWithStats.bcc.join(', ')}
            </Text>
          )
        }
      </Flex>
      <Flex
        vertical={isMobile}
        justify="space-between"
        align="flex-start"
        gap={4}
      >
        <Paragraph style={{ height: open ? 'auto' : '20px', overflow: 'hidden' }}>
          <Text className="mr-2" style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            {(emailWithStats.from || emailWithStats.to[0])?.split('@')[0]}
          </Text>

          <span
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ fontSize: 12 }}
          />

        </Paragraph>
        {
          !open && html.length >= 70 && (
            <Button type="link" className="pr-0">
              Show more
            </Button>
          )
        }
      </Flex>

    </Flex>
  );
}

export default EmailEvent;
